import { createClient } from '@supabase/supabase-js';

// Local API mode detection
const useLocal = (import.meta.env?.VITE_USE_LOCAL_API === 'true') || (!import.meta.env?.VITE_SUPABASE_URL && !import.meta.env?.VITE_SUPABASE_ANON_KEY);
const apiBase = import.meta.env?.VITE_API_BASE_URL || 'http://localhost:4000/api';

// Environment variables validation with detailed debugging
const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;

if (!useLocal) {
  console.log('🔍 Supabase Environment Debug:', {
    url: supabaseUrl ? `${supabaseUrl?.substring(0, 20)}...` : 'UNDEFINED',
    key: supabaseAnonKey ? `${supabaseAnonKey?.substring(0, 20)}...` : 'UNDEFINED',
    urlType: typeof supabaseUrl,
    keyType: typeof supabaseAnonKey
  });
}

// Enhanced validation with specific error messages
if (!useLocal && !supabaseUrl) {
  console.error('❌ VITE_SUPABASE_URL está vazia ou não definida');
  throw new Error(
    '⚠️ Configuração Supabase: VITE_SUPABASE_URL não encontrada no arquivo .env\n' + '📝 Adicione: VITE_SUPABASE_URL=https://seu-projeto.supabase.co'
  );
}

if (!useLocal && !supabaseAnonKey) {
  console.error('❌ VITE_SUPABASE_ANON_KEY está vazia ou não definida');
  throw new Error(
    '⚠️ Configuração Supabase: VITE_SUPABASE_ANON_KEY não encontrada no arquivo .env\n' + '📝 Adicione: VITE_SUPABASE_ANON_KEY=sua-chave-anonima-aqui'
  );
}

// Trim whitespace and validate URL format
const cleanUrl = supabaseUrl?.trim();
const cleanKey = supabaseAnonKey?.trim();

// More robust URL validation
const isValidUrl = (url) => {
  try {
    const urlObj = new URL(url);
    return urlObj?.protocol === 'https:' || urlObj?.protocol === 'http:';
  } catch {
    return false;
  }
};

if (!useLocal && !isValidUrl(cleanUrl)) {
  console.error('❌ URL do Supabase inválida:', cleanUrl);
  throw new Error(
    '⚠️ VITE_SUPABASE_URL deve ser uma URL válida\n' + '✅ Exemplo correto: https://projeto.supabase.co\n' +
    `❌ URL fornecida: ${cleanUrl}`
  );
}

if (!useLocal) {
  console.log('✅ Configuração Supabase validada com sucesso');
}

// Create Supabase client with validated credentials
export const supabase = useLocal ? null : createClient(cleanUrl, cleanKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  },
  global: {
    headers: {
      'x-application-name': '21zero5'
    }
  }
});

  // Enhanced helper functions for common operations
  export const supabaseHelpers = {
  // Test connection
  async testConnection() {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('count', { count: 'exact', head: true });
      
      if (error) {
        console.warn('⚠️ Conexão Supabase estabelecida, mas tabela user_profiles não encontrada:', error?.message);
        return { connected: true, tableExists: false, error };
      }
      
      console.log('✅ Conexão Supabase e esquema validados');
      return { connected: true, tableExists: true, error: null };
    } catch (err) {
      console.error('❌ Erro na conexão Supabase:', err);
      return { connected: false, tableExists: false, error: err };
    }
  },

  // Marketplace CRUD
  async createMarketplace(marketplaceData) {
    try {
      if (useLocal) {
        const res = await fetch(`${apiBase}/marketplaces`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(marketplaceData) });
        if (!res.ok) return { data: null, error: new Error(await res.text()) };
        const data = await res.json();
        return { data, error: null };
      }
      const { data, error } = await supabase?.from('marketplaces')?.insert([marketplaceData])?.select()?.single();
      if (!error) {
        console.log('✅ Marketplace criado:', data?.name);
      }
      return { data, error };
    } catch (error) {
      console.error('❌ Erro ao criar marketplace:', error);
      return { data: null, error };
    }
  },

  async getUserMarketplaces(userId) {
    try {
      if (useLocal) {
        const res = await fetch(`${apiBase}/users/${userId || 'guest'}/marketplaces`);
        if (!res.ok) return { data: [], error: new Error(await res.text()) };
        const data = await res.json();
        return { data, error: null };
      }
      const { data, error } = await supabase?.from('marketplaces')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false });
      return { data, error };
    } catch (error) {
      console.error('❌ Erro ao buscar marketplaces:', error);
      return { data: [], error };
    }
  },

  async updateMarketplace(marketplaceId, updates) {
    try {
      if (useLocal) {
        const res = await fetch(`${apiBase}/marketplaces/${marketplaceId}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(updates) });
        if (!res.ok) return { data: null, error: new Error(await res.text()) };
        const data = await res.json();
        return { data, error: null };
      }
      const { data, error } = await supabase?.from('marketplaces')?.update(updates)?.eq('id', marketplaceId)?.select()?.single();
      if (!error) {
        console.log('✅ Marketplace atualizado:', marketplaceId);
      }
      return { data, error };
    } catch (error) {
      console.error('❌ Erro ao atualizar marketplace:', error);
      return { data: null, error };
    }
  },

  async deleteMarketplace(marketplaceId) {
    try {
      if (useLocal) {
        const res = await fetch(`${apiBase}/marketplaces/${marketplaceId}`, { method: 'DELETE' });
        if (!res.ok) return { data: null, error: new Error(await res.text()) };
        const data = await res.json();
        return { data, error: null };
      }
      const { data, error } = await supabase?.from('marketplaces')?.delete()?.eq('id', marketplaceId);
      if (!error) {
        console.log('🗑️ Marketplace removido:', marketplaceId);
      }
      return { data, error };
    } catch (error) {
      console.error('❌ Erro ao deletar marketplace:', error);
      return { data: null, error };
    }
  },

  // Marketplace stats helper
  async getMarketplaceStats(marketplaceId, userId) {
    try {
      if (useLocal) {
        const url = new URL(`${apiBase}/marketplaces/${marketplaceId}/stats`);
        if (userId) url.searchParams.set('userId', userId);
        const res = await fetch(url.toString());
        if (!res.ok) return { data: null, error: new Error(await res.text()) };
        const data = await res.json();
        return { data, error: null };
      }
      // Supabase fallback: requires proper RPC or joins; returning zeroes for now
      return { data: { usageCount: 0, totalValue: 0 }, error: null };
    } catch (error) {
      console.error('❌ Erro ao obter estatísticas de marketplace:', error);
      return { data: null, error };
    }
  },

  // Project marketplaces pivot
  async addProjectMarketplace(relData) {
    try {
      if (useLocal) {
        const res = await fetch(`${apiBase}/project_marketplaces`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(relData) });
        if (!res.ok) return { data: null, error: new Error(await res.text()) };
        const data = await res.json();
        return { data, error: null };
      }
      const { data, error } = await supabase?.from('project_marketplaces')?.insert([relData])?.select()?.single();
      return { data, error };
    } catch (error) {
      console.error('❌ Erro ao adicionar marketplace ao projeto:', error);
      return { data: null, error };
    }
  },

  async removeProjectMarketplace(relId) {
    try {
      if (useLocal) {
        const res = await fetch(`${apiBase}/project_marketplaces/${relId}`, { method: 'DELETE' });
        if (!res.ok) return { data: null, error: new Error(await res.text()) };
        const data = await res.json();
        return { data, error: null };
      }
      const { data, error } = await supabase?.from('project_marketplaces')?.delete()?.eq('id', relId);
      return { data, error };
    } catch (error) {
      console.error('❌ Erro ao remover marketplace do projeto:', error);
      return { data: null, error };
    }
  },

  // Auth helpers
  async signUp(email, password, userData = {}) {
    try {
      if (useLocal) {
        const apiBase = import.meta?.env?.VITE_API_BASE_URL || '/api';
        const res = await fetch(`${apiBase}/auth/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: userData?.name, email, password })
        });
        const json = await res.json();
        if (!res.ok || json?.error) {
          return { data: null, error: { message: json?.error || 'Erro no cadastro' } };
        }
        const user = json?.user;
        // Persistência simples de sessão local (apenas usuário, sem JWT)
        try { localStorage.setItem('session_user', JSON.stringify(user)); } catch {}
        return { data: { user }, error: null };
      }
      const result = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: userData
        }
      });
      console.log('🔐 Cadastro realizado:', result?.data?.user ? 'Sucesso' : 'Falhou');
      return result;
    } catch (error) {
      console.error('❌ Erro no cadastro:', error);
      throw error;
    }
  },

  async signIn(email, password) {
    try {
      if (useLocal) {
        const apiBase = import.meta?.env?.VITE_API_BASE_URL || '/api';
        const res = await fetch(`${apiBase}/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password })
        });
        const json = await res.json();
        if (!res.ok || json?.error) {
          return { data: null, error: { message: json?.error || 'Erro no login' } };
        }
        const user = json?.user;
        try { localStorage.setItem('session_user', JSON.stringify(user)); } catch {}
        return { data: { user }, error: null };
      }
      const result = await supabase?.auth?.signInWithPassword({ email, password });
      console.log('🔓 Login realizado:', result?.data?.user ? 'Sucesso' : 'Falhou');
      return result;
    } catch (error) {
      console.error('❌ Erro no login:', error);
      throw error;
    }
  },

  async signOut() {
    try {
      if (useLocal) {
        return { error: null };
      }
      const result = await supabase?.auth?.signOut();
      console.log('🚪 Logout realizado');
      return result;
    } catch (error) {
      console.error('❌ Erro no logout:', error);
      throw error;
    }
  },

  // OAuth login helper (e.g., Google)
  async signInWithOAuth(provider, { redirectTo } = {}) {
    try {
      if (useLocal) {
        // No redirect in local mode; simulate success
        return { data: { provider, url: null }, error: null };
      }
      const result = await supabase?.auth?.signInWithOAuth({
        provider,
        options: {
          redirectTo: redirectTo || (typeof window !== 'undefined' ? window.location.origin : undefined)
        }
      });
      console.log(`🔗 OAuth (${provider}) iniciado`, result?.data?.url ? 'com redirect' : 'sem redirect');
      return result;
    } catch (error) {
      console.error(`❌ Erro no OAuth (${provider}):`, error);
      throw error;
    }
  },

  async getCurrentUser() {
    try {
      if (useLocal) {
        return { id: 'guest', email: 'guest@local' };
      }
      const { data: { user } } = await supabase?.auth?.getUser();
      return user;
    } catch (error) {
      console.error('❌ Erro ao obter usuário atual:', error);
      return null;
    }
  },

  async getUserProfile(userId) {
    try {
      if (useLocal) {
        const res = await fetch(`${apiBase}/user_profiles/${userId || 'guest'}`);
        if (!res.ok) return { data: null, error: new Error(await res.text()) };
        const data = await res.json();
        return { data, error: null };
      }
      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single();
      
      if (error && error?.code !== 'PGRST116') { // PGRST116 = no rows returned
        console.error('❌ Erro ao buscar perfil:', error);
      }
      
      return { data, error };
    } catch (error) {
      console.error('❌ Erro na busca do perfil:', error);
      return { data: null, error };
    }
  },

  // Storage helpers with error handling
  async uploadFile(bucket, filePath, file) {
    try {
      const { data, error } = await supabase?.storage?.from(bucket)?.upload(filePath, file, {
        cacheControl: '3600',
        upsert: true
      });
      
      if (error) {
        console.error(`❌ Erro no upload para ${bucket}/${filePath}:`, error);
      } else {
        console.log(`✅ Arquivo enviado: ${bucket}/${filePath}`);
      }
      
      return { data, error };
    } catch (error) {
      console.error('❌ Erro crítico no upload:', error);
      return { data: null, error };
    }
  },

  async getSignedUrl(bucket, filePath, expiresIn = 3600) {
    try {
      const { data, error } = await supabase?.storage?.from(bucket)?.createSignedUrl(filePath, expiresIn);
      return { data, error };
    } catch (error) {
      console.error('❌ Erro ao gerar URL assinada:', error);
      return { data: null, error };
    }
  },

  async deleteFile(bucket, filePath) {
    try {
      const { data, error } = await supabase?.storage?.from(bucket)?.remove([filePath]);
      
      if (!error) {
        console.log(`🗑️ Arquivo removido: ${bucket}/${filePath}`);
      }
      
      return { data, error };
    } catch (error) {
      console.error('❌ Erro ao deletar arquivo:', error);
      return { data: null, error };
    }
  },

  // Printer CRUD with enhanced error handling
  async createPrinter(printerData) {
    try {
      if (useLocal) {
        const resp = await fetch(`${apiBase}/printers`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(printerData)
        });
        const data = await resp.json().catch(() => null);
        const error = resp.ok ? null : (data?.error || 'Erro ao criar impressora');
        if (!error) console.log('✅ Impressora criada (local):', data?.name);
        return { data, error };
      }

      const { data, error } = await supabase?.from('printers')?.insert([printerData])?.select()?.single();
      if (!error) {
        console.log('✅ Impressora criada:', data?.name);
      }
      return { data, error };
    } catch (error) {
      console.error('❌ Erro ao criar impressora:', error);
      return { data: null, error };
    }
  },

  async getUserPrinters(userId) {
    try {
      if (useLocal) {
        const resp = await fetch(`${apiBase}/users/${userId || 'guest'}/printers`);
        const data = await resp.json().catch(() => []);
        const error = resp.ok ? null : 'Erro ao buscar impressoras';
        return { data, error };
      }

      const { data, error } = await supabase?.from('printers')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false });
      return { data, error };
    } catch (error) {
      console.error('❌ Erro ao buscar impressoras:', error);
      return { data: [], error };
    }
  },

  async updatePrinter(printerId, updates) {
    try {
      if (useLocal) {
        const resp = await fetch(`${apiBase}/printers/${printerId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updates)
        });
        const data = await resp.json().catch(() => null);
        const error = resp.ok ? null : (data?.error || 'Erro ao atualizar impressora');
        if (!error) console.log('✅ Impressora atualizada (local):', printerId);
        return { data, error };
      }

      const { data, error } = await supabase?.from('printers')?.update(updates)?.eq('id', printerId)?.select()?.single();
      if (!error) {
        console.log('✅ Impressora atualizada:', printerId);
      }
      return { data, error };
    } catch (error) {
      console.error('❌ Erro ao atualizar impressora:', error);
      return { data: null, error };
    }
  },

  async deletePrinter(printerId) {
    try {
      if (useLocal) {
        const resp = await fetch(`${apiBase}/printers/${printerId}`, { method: 'DELETE' });
        const data = await resp.json().catch(() => null);
        const error = resp.ok ? null : (data?.error || 'Erro ao deletar impressora');
        if (!error) console.log('🗑️ Impressora removida (local):', printerId);
        return { data, error };
      }

      const { data, error } = await supabase?.from('printers')?.delete()?.eq('id', printerId);
      if (!error) {
        console.log('🗑️ Impressora removida:', printerId);
      }
      return { data, error };
    } catch (error) {
      console.error('❌ Erro ao deletar impressora:', error);
      return { data: null, error };
    }
  },

  // Enhanced filament helpers with history tracking
  async getFilamentUsageHistory(filamentId) {
    try {
      // Fallback seguro em modo local: retorna lista vazia para não quebrar a UI
      if (useLocal) {
        return { data: [], error: null };
      }

      const query = supabase?.from('filament_usage_history')?.select(`
        *,
        project:printing_projects(id, name, created_at, image_urls),
        filament:filaments(brand, material, color)
      `)?.eq('filament_id', filamentId)?.order('usage_date', { ascending: false });

      const { data, error } = query ? await query : { data: [], error: null };
      return { data, error };
    } catch (error) {
      console.error('❌ Erro ao buscar histórico de uso do filamento:', error);
      return { data: [], error };
    }
  },

  async createFilamentUsage(usageData) {
    const { data, error } = await supabase?.from('filament_usage_history')?.insert(usageData)?.select();
    
    return { data, error };
  },

  // Printer manufacturer and model helpers
  async getPrinterManufacturers() {
    if (useLocal) {
      return { data: [], error: null };
    }
    const { data, error } = await supabase?.from('printer_manufacturers')?.select('*')?.order('name');
    return { data, error };
  },

  async getPrinterModelsByManufacturer(manufacturerId) {
    if (useLocal) {
      return { data: [], error: null };
    }
    const { data, error } = await supabase?.from('printer_models')?.select('*')?.eq('manufacturer_id', manufacturerId)?.order('name');
    return { data, error };
  },

  async getPrinterModel(modelId) {
    if (useLocal) {
      return { data: null, error: null };
    }
    const { data, error } = await supabase?.from('printer_models')?.select(`
        *,
        manufacturer:printer_manufacturers(name, logo_url)
      `)?.eq('id', modelId)?.single();
    return { data, error };
  },

  // Enhanced printer management
  async updatePrinterStatus(printerId, newStatus, reason = null) {
    if (useLocal) {
      console.log('ℹ️ Atualização de status (local stub):', { printerId, newStatus, reason });
      return { data: { success: true }, error: null };
    }
    const { data, error } = await supabase?.rpc('update_printer_status', {
      printer_uuid: printerId,
      new_status_value: newStatus,
      reason_text: reason
    });
    return { data, error };
  },

  async getPrinterStatusHistory(printerId) {
    if (useLocal) {
      return { data: [], error: null };
    }
    const { data, error } = await supabase?.from('printer_status_history')?.select(`
        *,
        changed_by_user:user_profiles(full_name, email)
      `)?.eq('printer_id', printerId)?.order('changed_at', { ascending: false });
    return { data, error };
  },

  // Enhanced project management with advanced costing
  async createProjectWithAdvancedCosting(projectData) {
    try {
      if (useLocal) {
        const payload = {
          ...projectData,
          printer_purchase_cost: projectData?.printer_purchase_cost ?? 0,
          expected_printer_lifetime_years: projectData?.expected_printer_lifetime_years ?? 3,
          daily_usage_hours: projectData?.daily_usage_hours ?? 6.0,
          repair_cost_percentage: projectData?.repair_cost_percentage ?? 5.0,
          setup_time_minutes: projectData?.setup_time_minutes ?? 15,
          setup_cost_per_hour: projectData?.setup_cost_per_hour ?? 25.0,
          postprocessing_time_minutes: projectData?.postprocessing_time_minutes ?? 30,
          postprocessing_cost_per_hour: projectData?.postprocessing_cost_per_hour ?? 25.0,
          other_costs: projectData?.other_costs ?? 0,
          vat_percentage: projectData?.vat_percentage ?? 0
        };
        const resp = await fetch(`${apiBase}/projects`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        const data = await resp.json().catch(() => null);
        const error = resp.ok ? null : (data?.error || 'Erro ao criar projeto (local)');
        if (!error) console.log('✅ Projeto criado (local):', data?.name);
        return { data, error };
      }
      const { data, error } = await supabase?.from('printing_projects')?.insert({
        ...projectData,
        printer_purchase_cost: projectData?.printer_purchase_cost || 0,
        expected_printer_lifetime_years: projectData?.expected_printer_lifetime_years || 3,
        daily_usage_hours: projectData?.daily_usage_hours || 6.0,
        repair_cost_percentage: projectData?.repair_cost_percentage || 5.0,
        setup_time_minutes: projectData?.setup_time_minutes || 15,
        setup_cost_per_hour: projectData?.setup_cost_per_hour || 25.0,
        postprocessing_time_minutes: projectData?.postprocessing_time_minutes || 30,
        postprocessing_cost_per_hour: projectData?.postprocessing_cost_per_hour || 25.0,
        other_costs: projectData?.other_costs || 0,
        vat_percentage: projectData?.vat_percentage || 0
      })?.select()?.single();
      return { data, error };
    } catch (error) {
      console.error('❌ Erro ao criar projeto com custos avançados:', error);
      return { data: null, error };
    }
  },

  // Image upload helpers
  async uploadPrinterImage(file, printerId) {
    if (useLocal) {
      // Sem armazenamento local real: retorna placeholder
      return { publicUrl: 'https://placehold.co/600x400?text=Printer', filePath: null };
    }
    const fileExt = file?.name?.split('.')?.pop();
    const fileName = `${printerId}/${Date.now()}.${fileExt}`;
    const filePath = `printers/${fileName}`;
    const { error: uploadError } = await supabase?.storage?.from('printer-images')?.upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });
    if (uploadError) throw uploadError;
    const { data: urlData } = supabase?.storage?.from('printer-images')?.getPublicUrl(filePath);
    return { publicUrl: urlData?.publicUrl, filePath };
  },

  async uploadFilamentImage(file, filamentId) {
    if (useLocal) {
      return { publicUrl: 'https://placehold.co/600x400?text=Filament', filePath: null };
    }
    const fileExt = file?.name?.split('.')?.pop();
    const fileName = `${filamentId}/${Date.now()}.${fileExt}`;
    const filePath = `filaments/${fileName}`;
    const { error: uploadError } = await supabase?.storage?.from('filament-images')?.upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });
    if (uploadError) throw uploadError;
    const { data: urlData } = supabase?.storage?.from('filament-images')?.getPublicUrl(filePath);
    return { publicUrl: urlData?.publicUrl, filePath };
  },

  async uploadProjectImage(file, projectId) {
    try {
      if (!projectId) throw new Error('projectId é obrigatório');
      if (useLocal) {
        // Armazena em memória como data URL para pré-visualização local
        const dataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        window.__projectImages = window.__projectImages || {};
        window.__projectImages[projectId] = [...(window.__projectImages[projectId] || []), dataUrl];
        return { publicUrl: dataUrl, filePath: null };
      }
      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `${projectId}/${Date.now()}.${fileExt}`;
      const filePath = `projects/${fileName}`;
      const { error: uploadError } = await supabase?.storage?.from('project-images')?.upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });
      if (uploadError) throw uploadError;
      const { data: urlData } = supabase?.storage?.from('project-images')?.getPublicUrl(filePath);
      return { publicUrl: urlData?.publicUrl, filePath };
    } catch (error) {
      console.error('❌ Erro no upload de imagem do projeto:', error);
      return { publicUrl: null, filePath: null, error };
    }
  },

  async deleteImage(bucketName, filePath) {
    const { error } = await supabase?.storage?.from(bucketName)?.remove([filePath]);
    
    return { error };
  },

  // Filament CRUD with enhanced error handling
  async createFilament(filamentData) {
    try {
      if (useLocal) {
        const resp = await fetch(`${apiBase}/filaments`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(filamentData)
        });
        const data = await resp.json().catch(() => null);
        const error = resp.ok ? null : (data?.error || 'Erro ao criar filamento');
        if (!error) console.log('✅ Filamento criado (local):', `${data?.brand} ${data?.material} ${data?.color}`);
        return { data, error };
      }

      const { data, error } = await supabase?.from('filaments')?.insert([filamentData])?.select()?.single();
      if (!error) {
        console.log('✅ Filamento criado:', `${data?.brand} ${data?.material} ${data?.color}`);
      }
      return { data, error };
    } catch (error) {
      console.error('❌ Erro ao criar filamento:', error);
      return { data: null, error };
    }
  },

  async getUserFilaments(userId) {
    try {
      if (useLocal) {
        const resp = await fetch(`${apiBase}/users/${userId || 'guest'}/filaments`);
        const data = await resp.json().catch(() => []);
        const error = resp.ok ? null : 'Erro ao buscar filamentos';
        return { data, error };
      }

      const { data, error } = await supabase?.from('filaments')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false });
      return { data, error };
    } catch (error) {
      console.error('❌ Erro ao buscar filamentos:', error);
      return { data: [], error };
    }
  },

  async updateFilament(filamentId, updates) {
    try {
      if (useLocal) {
        const resp = await fetch(`${apiBase}/filaments/${filamentId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updates)
        });
        const data = await resp.json().catch(() => null);
        const error = resp.ok ? null : (data?.error || 'Erro ao atualizar filamento');
        if (!error) console.log('✅ Filamento atualizado (local):', filamentId);
        return { data, error };
      }

      const { data, error } = await supabase?.from('filaments')?.update(updates)?.eq('id', filamentId)?.select()?.single();
      if (!error) {
        console.log('✅ Filamento atualizado:', filamentId);
      }
      return { data, error };
    } catch (error) {
      console.error('❌ Erro ao atualizar filamento:', error);
      return { data: null, error };
    }
  },

  async deleteFilament(filamentId) {
    try {
      if (useLocal) {
        const resp = await fetch(`${apiBase}/filaments/${filamentId}`, { method: 'DELETE' });
        const data = await resp.json().catch(() => null);
        const error = resp.ok ? null : (data?.error || 'Erro ao deletar filamento');
        if (!error) console.log('🗑️ Filamento removido (local):', filamentId);
        return { data, error };
      }

      const { data, error } = await supabase?.from('filaments')?.delete()?.eq('id', filamentId);
      if (!error) {
        console.log('🗑️ Filamento removido:', filamentId);
      }
      return { data, error };
    } catch (error) {
      console.error('❌ Erro ao deletar filamento:', error);
      return { data: null, error };
    }
  },

  // Project CRUD with enhanced error handling
  async createProject(projectData) {
    try {
      if (useLocal) {
        const resp = await fetch(`${apiBase}/projects`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(projectData)
        });
        const data = await resp.json().catch(() => null);
        const error = resp.ok ? null : (data?.error || 'Erro ao criar projeto');
        if (!error) console.log('✅ Projeto criado (local):', data?.name);
        return { data, error };
      }
      const { data, error } = await supabase?.from('printing_projects')?.insert([projectData])?.select()?.single();
      if (!error) {
        console.log('✅ Projeto criado:', data?.name);
      }
      return { data, error };
    } catch (error) {
      console.error('❌ Erro ao criar projeto:', error);
      return { data: null, error };
    }
  },

  async getUserProjects(userId) {
    try {
      if (useLocal) {
        const resp = await fetch(`${apiBase}/users/${userId || 'guest'}/projects`);
        const data = await resp.json().catch(() => []);
        const error = resp.ok ? null : 'Erro ao buscar projetos';
        return { data, error };
      }
      const { data, error } = await supabase?.from('printing_projects')?.select(`
        *,
        printer:printers(name, brand, model),
        project_filaments(
          *,
          filament:filaments(brand, material, color, color_hex, price_per_kg)
        )
      `)?.eq('user_id', userId)?.order('created_at', { ascending: false });
      return { data, error };
    } catch (error) {
      console.error('❌ Erro ao buscar projetos:', error);
      return { data: [], error };
    }
  },

  async updateProject(projectId, updates) {
    try {
      if (useLocal) {
        const resp = await fetch(`${apiBase}/projects/${projectId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updates)
        });
        const data = await resp.json().catch(() => null);
        const error = resp.ok ? null : (data?.error || 'Erro ao atualizar projeto');
        if (!error) console.log('✅ Projeto atualizado (local):', projectId);
        return { data, error };
      }
      const { data, error } = await supabase?.from('printing_projects')?.update(updates)?.eq('id', projectId)?.select()?.single();
      if (!error) {
        console.log('✅ Projeto atualizado:', projectId);
      }
      return { data, error };
    } catch (error) {
      console.error('❌ Erro ao atualizar projeto:', error);
      return { data: null, error };
    }
  },

  async deleteProject(projectId) {
    try {
      if (useLocal) {
        const resp = await fetch(`${apiBase}/projects/${projectId}`, { method: 'DELETE' });
        const data = await resp.json().catch(() => null);
        const error = resp.ok ? null : (data?.error || 'Erro ao deletar projeto');
        if (!error) console.log('🗑️ Projeto removido (local):', projectId);
        return { data, error };
      }
      const { data, error } = await supabase?.from('printing_projects')?.delete()?.eq('id', projectId);
      if (!error) {
        console.log('🗑️ Projeto removido:', projectId);
      }
      return { data, error };
    } catch (error) {
      console.error('❌ Erro ao deletar projeto:', error);
      return { data: null, error };
    }
  },

  // Project filaments
  async addProjectFilament(projectFilamentData) {
    try {
      if (useLocal) {
        // Map common fields to local API schema (weight/cost)
        const payload = {
          project_id: projectFilamentData?.project_id,
          filament_id: projectFilamentData?.filament_id,
          // Local API expects `weight` (grams) and `cost`
          weight: Number(projectFilamentData?.weight_grams ?? projectFilamentData?.weight ?? 0),
          cost: Number(projectFilamentData?.cost_amount ?? projectFilamentData?.cost ?? 0),
          // Preserve any additional metadata like waste_grams, notes, etc.
          ...projectFilamentData
        };
        const resp = await fetch(`${apiBase}/project_filaments`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        const data = await resp.json().catch(() => null);
        const error = resp.ok ? null : (data?.error || 'Erro ao adicionar filamento ao projeto');
        return { data, error };
      }
      // Supabase: limit payload to known columns to avoid insert failures
      const supabasePayload = {
        project_id: projectFilamentData?.project_id,
        filament_id: projectFilamentData?.filament_id,
        weight_grams: Number(projectFilamentData?.weight_grams ?? 0),
        cost_amount: Number(projectFilamentData?.cost_amount ?? 0)
      };
      const { data, error } = await supabase?.from('project_filaments')?.insert([supabasePayload])?.select()?.single();
      return { data, error };
    } catch (error) {
      console.error('❌ Erro ao adicionar filamento ao projeto:', error);
      return { data: null, error };
    }
  },

  async removeProjectFilament(projectFilamentId) {
    try {
      if (useLocal) {
        const resp = await fetch(`${apiBase}/project_filaments/${projectFilamentId}`, { method: 'DELETE' });
        const data = await resp.json().catch(() => null);
        const error = resp.ok ? null : (data?.error || 'Erro ao remover filamento do projeto');
        return { data, error };
      }
      const { data, error } = await supabase?.from('project_filaments')?.delete()?.eq('id', projectFilamentId);
      return { data, error };
    } catch (error) {
      console.error('❌ Erro ao remover filamento do projeto:', error);
      return { data: null, error };
    }
  },

  // Analytics helpers with enhanced calculations
  async getUserAnalytics(userId) {
    try {
      if (useLocal) {
        const resp = await fetch(`${apiBase}/users/${userId || 'guest'}/projects`);
        const projects = await resp.json().catch(() => []);
        if (!resp.ok) {
          const error = 'Erro ao buscar análise';
          return { data: null, error };
        }
        const totalRevenue = projects?.reduce((sum, p) => sum + (parseFloat(p?.final_price) || 0), 0) || 0;
        const totalProfit = projects?.reduce((sum, p) => sum + (parseFloat(p?.profit_amount) || 0), 0) || 0;
        const completedProjects = projects?.filter(p => p?.status === 'completed')?.length || 0;
        const activeProjects = projects?.filter(p => p?.status === 'in_progress')?.length || 0;
        console.log('📊 Análise calculada (local):', { totalRevenue, totalProfit, completedProjects, activeProjects });
        return {
          data: {
            totalRevenue,
            totalProfit,
            completedProjects,
            activeProjects,
            projects
          },
          error: null
        };
      }
      const { data: projects, error: projectsError } = await supabase?.from('printing_projects')?.select('final_price, profit_amount, status, created_at')?.eq('user_id', userId);
      if (projectsError) {
        console.error('❌ Erro ao buscar análise:', projectsError);
        return { data: null, error: projectsError };
      }
      const totalRevenue = projects?.reduce((sum, p) => sum + (parseFloat(p?.final_price) || 0), 0) || 0;
      const totalProfit = projects?.reduce((sum, p) => sum + (parseFloat(p?.profit_amount) || 0), 0) || 0;
      const completedProjects = projects?.filter(p => p?.status === 'completed')?.length || 0;
      const activeProjects = projects?.filter(p => p?.status === 'in_progress')?.length || 0;
      console.log('📊 Análise calculada:', { totalRevenue, totalProfit, completedProjects, activeProjects });
      return {
        data: {
          totalRevenue,
          totalProfit,
          completedProjects,
          activeProjects,
          projects
        },
        error: null
      };
    } catch (error) {
      console.error('❌ Erro crítico na análise:', error);
      return { data: null, error };
    }
  }
};

export default supabase;